// «ضمانت بازگشت وجه» — the parent's side of the 5-day money-back guarantee
// (backend/services/guarantee.py). Opened from the settings sheet: every covered
// plan with the time left, and the short MANDATORY exit questionnaire + card/Sheba
// that files the claim. Filing it stops the plan at once (the family is back on
// the free version); the admin reviews and transfers the money by hand.
import { useEffect, useState } from "react";
import { useLang } from "../../i18n.jsx";
import { Btn } from "../../admin/ui.jsx";
import { BACK_PRIORITY, useHostBackButton } from "../backbutton.js";
import { haptic } from "../host.js";
import { api } from "../api.js";
import { planLabel } from "../constants.js";
import { fmt, toFa } from "../format.js";
import { GuaranteeSeal } from "../ui.jsx";
import { C, FONT } from "../../design-system.js";

// "3 روز و 4 ساعت" — the time left, never seconds (it is a five-day window).
export function guaranteeLeft(seconds, t) {
  const h = Math.floor((seconds || 0) / 3600);
  const d = Math.floor(h / 24), hh = h % 24;
  if (d > 0) return hh ? t("{d} روز و {h} ساعت", { d: toFa(d), h: toFa(hh) }) : t("{d} روز", { d: toFa(d) });
  if (h > 0) return t("{h} ساعت", { h: toFa(h) });
  return t("کمتر از یک ساعت");
}

const CLAIM_STATUS = {
  requested: ["درخواست شما ثبت شده و در حال بررسی است.", C.gold],
  approved: ["درخواست شما پذیرفته شد؛ مبلغ به‌زودی واریز می‌شود.", C.teal],
  paid: ["مبلغ بازگردانده شد.", C.teal],
  rejected: ["درخواست پذیرفته نشد و اشتراک دوباره فعال است.", C.red],
};

// A one-line summary for the settings row: the covered plan with the most time left.
export function useGuarantee() {
  const [data, setData] = useState(null);
  const load = () => api("/subscriptions/guarantee").then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  return [data, load];
}

// `claimFirst` opens the form on the first claimable plan (the preview gallery).
export function GuaranteeSheet({ onClose, onChanged, claimFirst = false }) {
  const { t } = useLang();
  const [data, load] = useGuarantee();
  const [claiming, setClaiming] = useState(null);     // the plan being claimed
  useEffect(() => {
    if (claimFirst && data && !claiming) setClaiming((data.plans || []).find(p => p.eligible) || null);
  }, [claimFirst, data]);
  useHostBackButton(() => (claiming ? setClaiming(null) : onClose()), { priority: BACK_PRIORITY.modal });

  const open = (data?.plans || []).filter(p => p.eligible);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 72, background: `${C.bgDeep}DD`, display: "flex", alignItems: "flex-end", justifyContent: "center", animation: "fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "90dvh", overflowY: "auto", padding: "22px 20px calc(30px + var(--tg-safe-bottom, 0px))", border: `1px solid ${C.borderGold}`, borderBottom: "none", boxSizing: "border-box", animation: "slideUp 0.3s ease" }}>
        <div style={{ width: 40, height: 4, background: C.border, borderRadius: 4, margin: "0 auto 18px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <GuaranteeSeal size={52} days={data?.days || 5} />
          <div>
            <h2 style={{ color: C.cream, fontSize: 18, fontWeight: 800, margin: 0 }}>{t("ضمانت بازگشت وجه")}</h2>
            <p style={{ color: C.textMid, fontSize: 12.5, margin: "3px 0 0", lineHeight: 1.7 }}>
              {t("طرح‌های دو و سه ماهه با پرداخت یک‌جا، تا {n} روز پس از پرداخت؛ یک بار برای هر حساب.", { n: toFa(data?.days || 5) })}
            </p>
          </div>
        </div>

        {!data && <div style={{ color: C.textMid, fontSize: 14, padding: "18px 0" }}>{t("در حال بارگذاری...")}</div>}

        {data && claiming ? (
          <ClaimForm plan={claiming} questions={data.questions} fee={data.fee}
            onCancel={() => setClaiming(null)}
            onDone={() => { setClaiming(null); load(); onChanged?.(); }} />
        ) : data && (
          <>
            {data.claim && (
              <div style={{ background: C.bgDeep, border: `1px solid ${(CLAIM_STATUS[data.claim.status] || [])[1] || C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 12, color: C.cream, fontSize: 14, lineHeight: 1.8 }}>
                {t((CLAIM_STATUS[data.claim.status] || [""])[0])}
                {data.claim.status === "paid" && data.claim.refund_amount > 0 && <> — {t("{n} تومان", { n: fmt(data.claim.refund_amount) })}</>}
                {data.claim.status === "rejected" && data.claim.admin_note && <div style={{ color: C.textMid, fontSize: 13 }}>{data.claim.admin_note}</div>}
              </div>
            )}
            {data.plans.length === 0 && !data.claim && (
              <div style={{ color: C.creamDim, fontSize: 14, lineHeight: 1.9, padding: "6px 0 10px" }}>
                {t("اشتراکی که شامل ضمانت باشد ندارید. این ضمانت برای طرح‌های دو و سه ماهه‌ای است که یک‌جا (نه قسطی) پرداخت شده باشند.")}
              </div>
            )}
            {data.plans.map(p => (
              <div key={p.subscription_id} style={{ background: C.bgDeep, border: `1px solid ${p.eligible ? C.borderGold : C.border}`, borderRadius: 14, padding: "13px 14px", marginBottom: 10 }}>
                <div style={{ color: C.cream, fontSize: 15, fontWeight: 700 }}>
                  {p.child_name ? `${p.child_name} · ` : ""}{t("طرح {plan}", { plan: planLabel(p.plan_months) })}
                </div>
                <div style={{ color: p.eligible ? C.gold : C.textMid, fontSize: 13.5, marginTop: 4 }}>
                  {p.eligible
                    ? (data.used ? t("ضمانت این حساب قبلاً استفاده شده است")
                      : t("{left} از مهلت ضمانت باقی مانده", { left: guaranteeLeft(p.remaining_seconds, t) }))
                    : p.status === "refund_requested" ? t("درخواست بازگشت وجه ثبت شده است")
                    : p.status === "refunded" ? t("وجه این اشتراک بازگردانده شده است")
                    : p.reason === "installment" ? t("خرید قسطی شامل ضمانت نیست")
                    : t("مهلت ضمانت این اشتراک تمام شده است")}
                </div>
                {p.eligible && !data.used && (
                  <Btn variant="outline" color={C.red} onClick={() => { haptic("light"); setClaiming(p); }} style={{ width: "100%", marginTop: 10 }}>
                    {t("درخواست بازگشت وجه")}
                  </Btn>
                )}
              </div>
            ))}
            {open.length > 0 && !data.used && (
              <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.8, marginTop: 4 }}>
                {t("با ثبت درخواست، اشتراک همان لحظه متوقف می‌شود. مبلغ پس از کسر {fee} تومان کارمزد (در خرید از بازار: کارمزد و مالیات بازار) بازگردانده می‌شود.", { fee: fmt(data.fee) })}
              </div>
            )}
            <Btn variant="outline" color={C.textMid} onClick={onClose} style={{ width: "100%", marginTop: 16 }}>{t("بستن")}</Btn>
          </>
        )}
      </div>
    </div>
  );
}

function ClaimForm({ plan, questions, fee, onCancel, onDone }) {
  const { t } = useLang();
  const [answers, setAnswers] = useState({});
  const [card, setCard] = useState("");
  const [holder, setHolder] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setAnswers(a => ({ ...a, [k]: v }));

  const missing = questions.some(q => q.required && (q.options ? !answers[q.id] : (answers[q.id] || "").trim().length < (q.min || 1)));
  const cardOk = !plan.needs_card || (card.replace(/[^0-9۰-۹]/g, "").length >= 16 && holder.trim().length >= 3);
  const ready = !missing && cardOk && agree && !busy;

  async function submit() {
    if (!ready) return;
    setBusy(true); setErr("");
    try {
      await api(`/subscriptions/guarantee/${plan.subscription_id}`, { method: "POST",
        body: JSON.stringify({ answers, card: plan.needs_card ? card : null, holder: plan.needs_card ? holder : null }) });
      haptic("success");
      onDone();
    } catch (e) {
      setErr(e.message || t("ثبت درخواست ناموفق بود. دوباره تلاش کنید."));
      setBusy(false);
    }
  }

  const field = { width: "100%", boxSizing: "border-box", background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", color: C.cream, fontFamily: FONT, fontSize: 15, outline: "none" };
  return (
    <div>
      <div style={{ color: C.creamDim, fontSize: 13.5, lineHeight: 1.85, marginBottom: 14 }}>
        {t("برای بهتر شدن ضحا قصه، لطفاً به این چند پرسش کوتاه پاسخ دهید. پاسخ به همهٔ آن‌ها برای ثبت درخواست لازم است.")}
      </div>
      {questions.map(q => (
        <div key={q.id} style={{ marginBottom: 14 }}>
          <div style={{ color: C.cream, fontSize: 14.5, fontWeight: 700, marginBottom: 8 }}>{q.label}{q.required && <span style={{ color: C.red }}> *</span>}</div>
          {q.options ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {q.options.map(o => {
                const on = answers[q.id] === o.key;
                return (
                  <button key={o.key} onClick={() => { haptic("selection"); set(q.id, o.key); }} className="press"
                    style={{ background: on ? `${C.gold}18` : C.bgDeep, border: `1.5px solid ${on ? C.gold : C.border}`, color: on ? C.goldLight : C.creamDim, borderRadius: 999, padding: "8px 13px", fontFamily: FONT, fontSize: 13.5, fontWeight: on ? 700 : 500, cursor: "pointer" }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <textarea value={answers[q.id] || ""} onChange={e => set(q.id, e.target.value)} rows={3}
              style={{ ...field, resize: "vertical", lineHeight: 1.8 }} />
          )}
        </div>
      ))}

      {plan.needs_card && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: C.cream, fontSize: 14.5, fontWeight: 700, marginBottom: 8 }}>{t("حساب واریز")}<span style={{ color: C.red }}> *</span></div>
          <input value={card} onChange={e => setCard(e.target.value)} dir={card ? "ltr" : "rtl"} inputMode="text" placeholder={t("شمارهٔ کارت یا شبا (IR…)")}
            style={{ ...field, textAlign: "center", letterSpacing: 1, marginBottom: 8 }} />
          <input value={holder} onChange={e => setHolder(e.target.value)} placeholder={t("نام و نام خانوادگی صاحب حساب")} style={field} />
        </div>
      )}

      <div style={{ background: `${C.gold}0F`, border: `1px solid ${C.borderGold}`, borderRadius: 12, padding: "11px 13px", color: C.creamDim, fontSize: 13, lineHeight: 1.85, marginBottom: 12 }}>
        {plan.stars
          ? t("مبلغ به‌صورت {n} ستاره به حساب تلگرام شما بازمی‌گردد.", { n: toFa(plan.stars) })
          : plan.refund != null
            ? t("مبلغ قابل بازگشت: {n} تومان ({paid} تومان پرداختی منهای {fee} تومان کارمزد).", { n: fmt(plan.refund), paid: fmt(plan.paid_total), fee: fmt(fee) })
            : t("مبلغ پرداختی پس از کسر کارمزد و مالیات کافه‌بازار بازگردانده می‌شود.")}
      </div>

      <label style={{ display: "flex", gap: 9, alignItems: "flex-start", color: C.cream, fontSize: 13.5, lineHeight: 1.8, cursor: "pointer", marginBottom: 12 }}>
        <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ accentColor: C.gold, width: 18, height: 18, marginTop: 4, flexShrink: 0 }} />
        <span>{t("می‌دانم که با ثبت درخواست، اشتراک همین حالا متوقف می‌شود و این ضمانت فقط یک بار برای حساب من قابل استفاده است.")}</span>
      </label>
      {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn variant="gold" onClick={submit} disabled={!ready} style={{ flex: 1 }}>{busy ? t("در حال ثبت...") : t("ثبت درخواست بازگشت وجه")}</Btn>
        <Btn variant="outline" color={C.textMid} onClick={onCancel}>{t("انصراف")}</Btn>
      </div>
    </div>
  );
}
